import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FiTrash2 } from "react-icons/fi";
import { MdDragIndicator } from "react-icons/md";

import type { CompactSelectOption } from "newui/components/ui/InputWithLabel";
import { cn } from "utils/helpers";

import type { ColumnConfig } from "../../SpreadsheetEditor";
import { ScheduleRowCell } from "./ScheduleRowCell";
import type { ScheduleRowDraft } from "./ScheduleRowsBody";

const STICKY_CELL_STYLE: React.CSSProperties = {
	position: "sticky",
	left: 0,
	zIndex: 5,
	background: "white",
};

const CELL_BASE = "border border-[#EAECF0] px-1 py-1 text-sm";

export interface ScheduleRowProps {
	row: ScheduleRowDraft;
	rowIndex: number;
	columns: ColumnConfig[];
	unitOptions: CompactSelectOption[];
	/** localIds of columns where this row's cell is invalid. */
	errorCellLocalIds?: Set<string>;
	onChangeCell: (rowId: number, columnLocalId: string, value: string) => void;
	onBlurCurrency: (rowId: number, columnLocalId: string) => void;
	onRemoveRow: (rowId: number) => void;
}

/**
 * Sortable schedule-of-rates row. Mirrors the visual conventions of the
 * QuoteTable's TemplateRow (sticky #/drag-handle cell, trailing delete
 * button) but renders editable cells against `row.values[columnLocalId]`.
 */
export const ScheduleRow: React.FC<ScheduleRowProps> = ({
	row,
	rowIndex,
	columns,
	unitOptions,
	errorCellLocalIds,
	onChangeCell,
	onBlurCurrency,
	onRemoveRow,
}) => {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id: String(row.id) });

	const style: React.CSSProperties = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	};

	return (
		<tr
			ref={setNodeRef}
			role="row"
			style={style}
			data-testid={`schedule-row-${row.id}`}
		>
			<td
				role="gridcell"
				style={STICKY_CELL_STYLE}
				className={cn(CELL_BASE, "w-14 bg-gray-50")}
			>
				<div className="flex items-center gap-1">
					<button
						type="button"
						className="cursor-grab text-tertiary-600 hover:text-primary-500 active:cursor-grabbing"
						aria-label={`Drag row ${rowIndex + 1}`}
						data-testid={`schedule-drag-row-${row.id}`}
						{...attributes}
						{...listeners}
					>
						<MdDragIndicator size={14} />
					</button>
					<span className="font-roboto text-xs text-primary-500">
						{rowIndex + 1}
					</span>
				</div>
			</td>
			{columns.map((column) => (
				<ScheduleRowCell
					key={column.localId}
					column={column}
					row={row}
					rowIndex={rowIndex}
					unitOptions={unitOptions}
					hasError={errorCellLocalIds?.has(column.localId) ?? false}
					onChangeCell={onChangeCell}
					onBlurCurrency={onBlurCurrency}
				/>
			))}
			<td role="gridcell" className="w-10 border-0 px-1">
				<button
					type="button"
					className="rounded p-1 text-red-400 hover:bg-red-50 hover:text-red-600"
					onClick={() => onRemoveRow(row.id)}
					title="Remove row"
					aria-label={`Remove row ${rowIndex + 1}`}
					data-testid={`schedule-remove-row-${row.id}`}
				>
					<FiTrash2 size={12} />
				</button>
			</td>
		</tr>
	);
};

export default ScheduleRow;
